'use client';

import { useEffect, useState, useCallback } from 'react';
import { useAccountSelection } from '@/contexts/account-selection-context';
import type { StreamerMessage, StreamerState } from '@/services/account-streamer';

export interface UseAccountStreamerOptions {
  autoConnect?: boolean;
  reconnectOnAccountChange?: boolean;
}

export function useAccountStreamer(options: UseAccountStreamerOptions = {}) {
  const { autoConnect = true, reconnectOnAccountChange = true } = options;
  const { selectedAccounts } = useAccountSelection();
  
  const [streamerState, setStreamerState] = useState<StreamerState>({
    isConnected: false,
    isConnecting: false,
    error: null,
  });
  
  const [lastMessage, setLastMessage] = useState<StreamerMessage | null>(null);
  const [messages, setMessages] = useState<StreamerMessage[]>([]);

  // OAuth keeps broker tokens in httpOnly cookies. Avoid constructing the SDK in
  // the browser; server API/SSE routes handle authenticated tastytrade access.
  useEffect(() => {
    if (!autoConnect || selectedAccounts.length === 0) {
      setStreamerState({
        isConnected: false,
        isConnecting: false,
        error: null,
      });
      return;
    }

    setStreamerState({
      isConnected: false,
      isConnecting: false,
      error: null,
    });
  }, [autoConnect, reconnectOnAccountChange, selectedAccounts]);

  const connect = useCallback(async (accountNumbers?: string[]) => {
    const numbers = accountNumbers || selectedAccounts.map(acc => acc.account['account-number']);
    if (numbers.length === 0) {
      return;
    }

    setStreamerState({
      isConnected: false,
      isConnecting: false,
      error: null,
    });
  }, [selectedAccounts]);

  useEffect(() => {
    if (!autoConnect || selectedAccounts.length === 0) {
      return;
    }

    const accountNumbers = selectedAccounts.map(acc => acc.account['account-number']);

    if (reconnectOnAccountChange) {
      void connect(accountNumbers);
    }
  }, [
    autoConnect,
    connect,
    reconnectOnAccountChange,
    selectedAccounts,
  ]);

  const disconnect = useCallback(async () => {
    setStreamerState({
      isConnected: false,
      isConnecting: false,
      error: null,
    });
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setLastMessage(null);
  }, []);

  const isRecord = useCallback(
    (value: unknown): value is Record<string, unknown> =>
      typeof value === 'object' && value !== null,
    [],
  );

  const hasNestedType = useCallback(
    (value: unknown, expected: string): boolean => {
      if (!isRecord(value)) {
        return false;
      }
      const type = typeof value.type === 'string' ? value.type : undefined;
      const event = typeof value.event === 'string' ? value.event : undefined;
      return type === expected || event === expected;
    },
    [isRecord],
  );

  // Extract specific data from messages
  const getPositionUpdates = useCallback(() => {
    return messages.filter(msg => 
      msg.type === 'position-update' || 
      msg.type === 'position' ||
      hasNestedType(msg.data, 'position')
    );
  }, [messages, hasNestedType]);

  const getBalanceUpdates = useCallback(() => {
    return messages.filter(msg => 
      msg.type === 'balance-update' || 
      msg.type === 'balance' ||
      hasNestedType(msg.data, 'balance')
    );
  }, [messages, hasNestedType]);

  const getOrderUpdates = useCallback(() => {
    return messages.filter(msg => 
      msg.type === 'order-update' || 
      msg.type === 'order' ||
      hasNestedType(msg.data, 'order')
    );
  }, [messages, hasNestedType]);

  return {
    // State
    isConnected: streamerState.isConnected,
    isConnecting: streamerState.isConnecting,
    error: streamerState.error,
    
    // Messages
    lastMessage,
    messages,
    
    // Actions
    connect,
    disconnect,
    clearMessages,
    
    // Filtered data
    positionUpdates: getPositionUpdates(),
    balanceUpdates: getBalanceUpdates(),
    orderUpdates: getOrderUpdates(),
  };
}
